import React, { useEffect, useState } from "react";
import {
  GiDiscGolfBag,
  GiHamburgerMenu,
} from "react-icons/gi";
import {
  FaHome,
  FaRegWindowClose,
} from "react-icons/fa";
import { PiNotebookFill } from "react-icons/pi";
import { RiMailOpenFill } from "react-icons/ri";
import { BsBank2 } from "react-icons/bs";
import { BiSolidPhoneCall } from "react-icons/bi";
import { MdKeyboardArrowDown } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import LogoColor from "../../Assets/images/logoTechmenteeColour.png";
import LogoWhite from "../../Assets/images/logoTechmentee.png";

import "./Navbar.css";

const MobileNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [hamburger, setHamburger] = useState(false);

  const [catagoriesData, setCatagoriesData] = useState([]);
  const [programData, setProgramData] = useState([]);
  const [serviceData, setServiceData] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  // --------------------------------------------------
  // Scroll Handler
  // --------------------------------------------------

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // --------------------------------------------------
  // Close mobile menu when route changes
  // --------------------------------------------------

  useEffect(() => {
    setHamburger(false);
  }, [location.pathname]);

  // --------------------------------------------------
  // Get Categories
  // --------------------------------------------------

  const getAllCategories = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/courses-category/all-categories`
      );

      setCatagoriesData(response?.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // --------------------------------------------------
  // Get Programs
  // --------------------------------------------------

  const getPrograms = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/program/get-all-program`
      );

      setProgramData(response?.data || []);
    } catch (error) {
      console.error("Error fetching programs:", error);
    }
  };

  // --------------------------------------------------
  // Get Services
  // --------------------------------------------------

  const getAllServices = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/services/service-name`
      );

      setServiceData(response?.data?.services || []);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  // --------------------------------------------------
  // API Calls
  // --------------------------------------------------

  useEffect(() => {
    getAllCategories();
    getPrograms();
    getAllServices();
  }, []);

  // --------------------------------------------------
  // Navigation Functions
  // --------------------------------------------------

  const closeMenu = () => {
    setHamburger(false);
  };

  const navigateToProgram = (program) => {
    navigate(`/knowledge/program/${program?._id}`);
    closeMenu();
  };

  const navigateToDetail = (course) => {
    navigate(`/course/${course?._id}`);
    closeMenu();
  };

  // --------------------------------------------------
  // Accordion Arrow
  // --------------------------------------------------

  const AccordionArrow = () => (
    <MdKeyboardArrowDown
      size={22}
      className="transition-transform duration-300 group-open:rotate-180"
    />
  );

  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <div className="lg:hidden block">
      {/* ================================================
          TOP INFORMATION BAR
      ================================================= */}

      <div className="bg-slate-700 text-white">
        <div className="flex flex-col items-center justify-center gap-2 px-5 py-3 border-b border-white/20 text-sm">

          <div className="text-center">
            <p>
              Welcome aboard!! Accelerate your career with Techmentee
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-8">

            <div className="flex items-center">
              <span>Call Us at&nbsp;</span>

              <a
                href="tel:+14707402777"
                className="hover:text-orange-400 transition"
              >
                +1 470 740 2777
              </a>
            </div>

            <a
              href="mailto:info@techmentee.com"
              className="hover:text-orange-400 transition"
            >
              info@techmentee.com
            </a>

          </div>
        </div>
      </div>

      {/* ================================================
          MOBILE NAVBAR
      ================================================= */}

      <nav
        className={`w-full px-5 ${
          isScrolled
            ? "bg-white text-black fixed top-0 left-0 z-[999] shadow-[rgba(0,_0,_0,_0.24)_0px_3px_8px]"
            : "bg-slate-700 text-white relative z-[999]"
        } transition-all duration-500`}
      >

        {/* ==============================================
            NAVBAR HEADER
        =============================================== */}

        <div className="flex items-center justify-between py-3">

          {/* Logo */}

          <Link to="/" onClick={closeMenu}>
            <img
              src={isScrolled ? LogoColor : LogoWhite}
              alt="Techmentee"
              className="w-[190px] sm:w-[210px]"
            />
          </Link>

          {/* Hamburger */}

          <button
            type="button"
            onClick={() => setHamburger((prev) => !prev)}
            className="flex items-center justify-center"
            aria-label={hamburger ? "Close menu" : "Open menu"}
          >
            {hamburger ? (
              <FaRegWindowClose size={30} />
            ) : (
              <GiHamburgerMenu size={30} />
            )}
          </button>
        </div>

        {/* ==============================================
            MOBILE MENU
        =============================================== */}

        <AnimatePresence>
          {hamburger && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className={`overflow-y-auto max-h-[75vh] pb-8 ${
                isScrolled ? "text-black" : "text-white"
              }`}
            >

              {/* ========================================
                  HOME
              ======================================== */}

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2 }}
                className="py-3 border-b border-gray-400/30"
              >
                <Link
                  to="/"
                  onClick={closeMenu}
                  className="flex items-center gap-3 font-semibold"
                >
                  <FaHome size={22} />
                  <span>Home</span>
                </Link>
              </motion.div>

              {/* ========================================
                  ABOUT
              ======================================== */}

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25 }}
                className="py-3 border-b border-gray-400/30"
              >
                <Link
                  to="/about/about-techmentee"
                  onClick={closeMenu}
                  className="flex items-center gap-3 font-semibold"
                >
                  <PiNotebookFill size={22} />
                  <span>About</span>
                </Link>
              </motion.div>

              {/* ========================================
                  SERVICES
              ======================================== */}

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="py-3 border-b border-gray-400/30"
              >
                <details className="group">

                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <div className="flex items-center gap-3 font-semibold">
                      <GiDiscGolfBag size={22} />
                      <span>Services</span>
                    </div>

                    <AccordionArrow />
                  </summary>

                  <div className="flex flex-col gap-3 mt-4 ml-8">

                    {/* Dynamic Services */}

                    {serviceData?.map((service) => (
                      <Link
                        key={service?._id}
                        to={`/serviceDetails/${service?._id}`}
                        onClick={closeMenu}
                        className="py-1 hover:text-orange-500 transition"
                      >
                        {service?.heading}
                      </Link>
                    ))}

                    {/* Static Immigration Support */}

                    <Link
                      to="/service/immigrationSupport"
                      onClick={closeMenu}
                      className="py-1 hover:text-orange-500 transition"
                    >
                      Immigration Support
                    </Link>

                  </div>
                </details>
              </motion.div>

              {/* ========================================
                  COURSES
              ======================================== */}

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35 }}
                className="py-3 border-b border-gray-400/30"
              >
                <details className="group">

                  <summary className="flex items-center justify-between cursor-pointer list-none">

                    <div className="flex items-center gap-3 font-semibold">
                      <RiMailOpenFill size={22} />
                      <span>Courses</span>
                    </div>

                    <AccordionArrow />

                  </summary>

                  <div className="flex flex-col gap-3 mt-4 ml-8">

                    {catagoriesData?.map((category) => (

                      <details
                        key={category?._id}
                        className="group"
                      >

                        {/* Category */}

                        <summary className="flex items-center justify-between cursor-pointer list-none py-2 px-3 rounded hover:bg-orange-500 hover:text-white transition">

                          <span className="font-medium">
                            {category?.categoryName}
                          </span>

                          <AccordionArrow />

                        </summary>

                        {/* Courses */}

                        <div className="flex flex-col gap-2 mt-2 ml-5">

                          {category?.courses?.map((course) => (

                            <button
                              key={course?._id}
                              type="button"
                              onClick={() => navigateToDetail(course)}
                              className="text-left py-1 hover:text-orange-500 transition"
                            >
                              {course?.title}
                            </button>

                          ))}

                        </div>

                      </details>

                    ))}

                  </div>
                </details>
              </motion.div>

              {/* ========================================
                  KNOWLEDGE CENTER
              ======================================== */}

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
                className="py-3 border-b border-gray-400/30"
              >
                <details className="group">

                  <summary className="flex items-center justify-between cursor-pointer list-none">

                    <div className="flex items-center gap-3 font-semibold">
                      <BsBank2 size={22} />
                      <span>Knowledge Center</span>
                    </div>

                    <AccordionArrow />

                  </summary>

                  <div className="flex flex-col gap-3 mt-4 ml-8">

                    {/* Process */}

                    <Link
                      to="/knowledge/process"
                      onClick={closeMenu}
                      className="py-1 hover:text-orange-500 transition"
                    >
                      Process
                    </Link>

                    {/* Programs */}

                    <details className="group">

                      <summary className="flex items-center justify-between cursor-pointer list-none py-2 px-3 rounded hover:bg-orange-500 hover:text-white transition">

                        <span className="font-medium">
                          Programs
                        </span>

                        <AccordionArrow />

                      </summary>

                      <div className="flex flex-col gap-2 mt-2 ml-5">

                        {programData?.map((program) => (

                          <button
                            key={program?._id}
                            type="button"
                            onClick={() => navigateToProgram(program)}
                            className="text-left py-1 hover:text-orange-500 transition"
                          >
                            {program?.title}
                          </button>

                        ))}

                        {/* Career Explorer */}

                        <Link
                          to="/knowledge/program/career-explorer"
                          onClick={closeMenu}
                          className="py-1 hover:text-orange-500 transition"
                        >
                          Career Explorer
                        </Link>

                      </div>

                    </details>

                  </div>
                </details>
              </motion.div>

              {/* ========================================
                  CONTACT US
              ======================================== */}

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.45 }}
                className="py-3 border-b border-gray-400/30"
              >
                <details className="group">

                  <summary className="flex items-center justify-between cursor-pointer list-none">

                    <div className="flex items-center gap-3 font-semibold">
                      <BiSolidPhoneCall size={22} />
                      <span>Contact Us</span>
                    </div>

                    <AccordionArrow />

                  </summary>

                  <div className="flex flex-col gap-3 mt-4 ml-8">

                    <Link
                      to="/contact-us/partner-with-us"
                      onClick={closeMenu}
                      className="py-1 hover:text-orange-500 transition"
                    >
                      Partner With Us
                    </Link>

                    <Link
                      to="/contact-us/request-for-service"
                      onClick={closeMenu}
                      className="py-1 hover:text-orange-500 transition"
                    >
                      Request For Service
                    </Link>

                    <Link
                      to="/contact-us/feedback"
                      onClick={closeMenu}
                      className="py-1 hover:text-orange-500 transition"
                    >
                      Feedback
                    </Link>

                    <Link
                      to="/contact-us/career"
                      onClick={closeMenu}
                      className="py-1 hover:text-orange-500 transition"
                    >
                      Career
                    </Link>

                  </div>
                </details>
              </motion.div>

              {/* ========================================
                  LOGIN
              ======================================== */}

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-6"
              >

                <a
                  href="https://techmentee-dashboard.vercel.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                  className="block w-full text-center bg-primaryOrangeColor hover:bg-orange-400 text-white font-bold px-6 py-3 rounded transition duration-300"
                >
                  Login
                </a>

              </motion.div>

            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </div>
  );
};

export default MobileNavbar;
